import { Fragment, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, Transition } from '@headlessui/react';
import { Sparkles, X, Check, Zap } from 'lucide-react';
import { creditApi } from '../../services/api';
import { toast } from 'sonner';

import { useTranslation } from 'react-i18next';
import { Spinner } from '@/components/ui/Spinner';
import { BrandLoader } from '@/components/ui/brand-loader';
interface Pack {
  id: string;
  label: string;
  credits: number;
  price_usd: number;
  bonus_pct: number;
}

interface AICreditTopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AICreditTopUpModal({ isOpen, onClose }: AICreditTopUpModalProps) {
    const { t } = useTranslation();
  const [packs, setPacks] = useState<Pack[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPack, setSelectedPack] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadPacks();
      
      // Initialize Paddle.js
      if (!window.Paddle) {
        const script = document.createElement('script');
        script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
        script.async = true;
        script.onload = () => {
          if (window.Paddle) {
            const paddleEnv = import.meta.env.VITE_PADDLE_ENVIRONMENT || 'sandbox';
            const paddleToken = import.meta.env.VITE_PADDLE_CLIENT_TOKEN;
            window.Paddle.Environment.set(paddleEnv);
            window.Paddle.Initialize({
              token: paddleToken,
              eventCallback: (data: any) => {
                console.log('Paddle TopUp event:', data);
                if (data?.name === 'checkout.completed') {
                  toast.success('Payment completed! Your top-up balance is updated.');
                  // Wait a moment for webhook to process, then reload
                  setTimeout(() => window.location.reload(), 2000);
                }
              },
            });
          }
        };
        document.head.appendChild(script);
      }
    }
  }, [isOpen]);

  const loadPacks = async () => {
    try {
      setIsLoading(true);
      const response = await creditApi.getPacks();
      if (response.success && response.data) {
        setPacks(response.data);
        if (response.data.length > 0) {
          // Pre-select the Standard pack
          const defaultPack = response.data.find(p => p.id === 'pack_5000') ?? response.data[0];
          setSelectedPack(defaultPack!.id);
        }
      }
    } catch (error) {
      console.error('Failed to load packs:', error);
      toast.error('Failed to load top-up options. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!selectedPack) return;
    
    setIsProcessing(true);
    try {
      const response = await creditApi.buyTopUp(selectedPack);
      
      if (response.success && response.data?.paddle_transaction_id) {
        onClose(); // Close modal first
        
        // Open Paddle Checkout
        if (window.Paddle) {
          window.Paddle.Checkout.open({
            transactionId: response.data.paddle_transaction_id,
          });
        } else if (response.data.approval_url) {
          window.open(response.data.approval_url, '_blank');
        } else {
          toast.error('Could not initiate checkout. Paddle JS not loaded.');
        }
      } else {
        toast.error(response.message || 'Failed to initialize checkout.');
      }
    } catch (error: any) {
      console.error('Purchase failed:', error);
      toast.error(error.response?.data?.message || error.message || 'Something went wrong.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (typeof document === 'undefined') return null;

  return createPortal(
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[10000]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 p-6 text-left align-middle shadow-2xl transition-all border border-gray-100 dark:border-gray-800">
                <button
                  onClick={onClose}
                  className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>

                <div className="text-center mb-8 mt-2">
                  <div className="mx-auto w-16 h-16 bg-gradient-to-tr from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center mb-4 shadow-lg shadow-purple-500/30">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <Dialog.Title
                    as="h3"
                    className="text-2xl font-bold text-gray-900 dark:text-white mb-2"
                  >{t('credits.topUpHeading')}</Dialog.Title>
                  <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                    Never run out of inspiration. Top-up credits never expire and are only used when your monthly allocation runs out.
                  </p>
                </div>

                {isLoading ? (
                  <div className="flex justify-center items-center py-20">
                    <BrandLoader size={48} label={t('common.loading')} />
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                    {packs.map((pack) => {
                      const isSelected = selectedPack === pack.id;
                      const isPopular = pack.id === 'pack_5000'; // Standard pack

                      return (
                        <div
                          key={pack.id}
                          onClick={() => setSelectedPack(pack.id)}
                          className={`relative cursor-pointer rounded-2xl p-5 transition-all duration-200 border-2 ${
                            isSelected
                              ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 shadow-md transform scale-[1.02]'
                              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-indigo-300 dark:hover:border-indigo-700'
                          }`}
                        >
                          {isPopular && (
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-sm whitespace-nowrap">{t('settings.billingSection.mostPopular')}</div>
                          )}

                          {isSelected && (
                            <div className="absolute top-3 right-3 text-indigo-500">
                              <Check className="w-5 h-5" />
                            </div>
                          )}

                          <div className="text-center mt-2">
                            <h4 className={`text-lg font-bold ${isSelected ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-900 dark:text-white'}`}>
                              {pack.label}
                            </h4>
                            
                            <div className="my-4 flex items-center justify-center space-x-1">
                              <Zap className={`w-5 h-5 ${isSelected ? 'text-indigo-500' : 'text-amber-500'}`} />
                              <span className="text-3xl font-extrabold text-gray-900 dark:text-white">
                                {pack.credits.toLocaleString()}
                              </span>
                            </div>

                            <div className="text-xl font-medium text-gray-600 dark:text-gray-300">
                              ${pack.price_usd.toFixed(2)}
                            </div>

                            {pack.bonus_pct > 0 && (
                              <div className="mt-3 inline-flex items-center space-x-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 px-2.5 py-1 rounded-full">
                                <span>+{pack.bonus_pct}% Bonus Credits</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="flex flex-col items-center">
                  <button
                    onClick={handlePurchase}
                    disabled={isProcessing || !selectedPack || isLoading}
                    className="w-full max-w-sm rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 text-base font-bold text-white shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {isProcessing ? (
                      <span className="flex items-center justify-center space-x-2">
                        <Spinner className="size-5" />
                        <span>{t('common.processing')}</span>
                      </span>
                    ) : (
                      'Purchase Credits securely via Paddle'
                    )}
                  </button>
                  <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 flex items-center">
                    <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2"></span>{t('credits.oneTimePayment')}</p>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>,
    document.body
  );
}
